import { Client, IMessage, StompConfig, StompSubscription } from '@stomp/stompjs';
import { useCallback, useEffect, useRef, useState } from 'react';
import SockJS from 'sockjs-client';

interface UseStompProps {
  brokerUrl: string;
  useSockJS?: boolean;
  connectHeaders?: Record<string, string>;
}

interface SubscriptionItem {
  topic: string;
  callback: (message: IMessage) => void;
  stompSubscription: StompSubscription | null;
}

export const useStomp = ({ brokerUrl, useSockJS = false, connectHeaders }: UseStompProps) => {
  const clientRef = useRef<Client | null>(null);
  const [isConnected, setIsConnected] = useState<boolean>(false);

  const subscriptionsRef = useRef<Map<string, SubscriptionItem>>(new Map());

  const tokenKey = connectHeaders?.Authorization || '';

  const safeParseBody = <T>(message: IMessage, callback: (parsed: T) => void) => {
    if (!message || !message.body || message.body === 'undefined' || message.body === 'null') {
      console.warn('[STOMP] 비어있거나 유효하지 않은 message.body가 수신되었습니다:', message);
      callback({} as T);
      return;
    }

    try {
      const parsedData: T = JSON.parse(message.body);
      callback(parsedData);
    } catch (error) {
      callback(message.body as unknown as T);
    }
  };

  useEffect(() => {
    const config: StompConfig = {
      connectHeaders: connectHeaders,
      debug: (str) => {
        if (process.env.NODE_ENV === 'development') {
          // console.log('[STOMP DEBUG]', str);
        }
      },
      reconnectDelay: 5000,
      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      onConnect: () => {
        setIsConnected(true);
        console.log('[STOMP] Connected successfully.');

        if (clientRef.current) {
          const client = clientRef.current;
          subscriptionsRef.current.forEach((item, topic) => {
            if (!item.stompSubscription) {
              const sub = client.subscribe(topic, item.callback);
              item.stompSubscription = sub;
            }
          });
        }
      },
      onDisconnect: () => {
        setIsConnected(false);
        console.log('[STOMP] Disconnected.');
        subscriptionsRef.current.forEach((item) => {
          item.stompSubscription = null;
        });
      },
      onStompError: (frame) => {
        console.error('STOMP Broker Error: ' + frame.headers['message']);
        console.error('Additional details: ' + frame.body);
      },
    };

    if (useSockJS) {
      config.webSocketFactory = () => new SockJS(brokerUrl);
    } else {
      config.brokerURL = brokerUrl;
    }

    const client = new Client(config);
    client.activate();
    clientRef.current = client;

    return () => {
      if (clientRef.current) {
        clientRef.current.deactivate();
        console.log('[STOMP] Connection deactivated.');
      }
    };
  }, [brokerUrl, useSockJS, tokenKey]);

  const subscribe = useCallback(<T>(topic: string, callback: (message: T) => void) => {
    if (subscriptionsRef.current.has(topic)) {
      return () => {
        /* */
      };
    }

    const handleMessage = (message: IMessage) => {
      safeParseBody(message, callback);
    };

    let stompSubscription: StompSubscription | null = null;

    if (clientRef.current && clientRef.current.connected) {
      stompSubscription = clientRef.current.subscribe(topic, handleMessage);
    }

    subscriptionsRef.current.set(topic, {
      topic,
      callback: handleMessage,
      stompSubscription,
    });

    return () => {
      const item = subscriptionsRef.current.get(topic);
      if (item?.stompSubscription) {
        item.stompSubscription.unsubscribe();
      }
      subscriptionsRef.current.delete(topic);
    };
  }, []);

  const publish = useCallback((destination: string, body: any, headers?: Record<string, string>) => {
    if (clientRef.current && clientRef.current.connected) {
      clientRef.current.publish({
        destination,
        body: typeof body === 'string' ? body : JSON.stringify(body),
        headers,
      });
    } else {
      console.warn('STOMP client가 아직 연결되지 않아 메시지를 발행할 수 없습니다.');
    }
  }, []);

  return { isConnected, subscribe, publish };
};
